import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Quote,
  Sparkles,
  ShieldCheck,
  MapPin,
  BookOpen,
} from "lucide-react";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";
import { WorryLessChapterOptIn } from "@/components/WorryLessChapterOptIn";
import WhatIsKAP from "@/components/WhatIsKAP";
import CareCreditBanner from "@/components/CareCreditBanner";

const LAURA_IMAGE =
  "https://customer-assets.emergentagent.com/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/l4ja8zkq_IMG_4558_Facetune_17-01-2023-21-22-22.jpeg";

const THEME_IMAGE =
  "https://images.unsplash.com/photo-1545389336-cf090694435e?w=1600&q=80&auto=format&fit=crop";

const IMPACT_STATEMENT =
  "I help high-achieving, hyper-independent women use Ketamine-Assisted Psychotherapy to rewire their nervous system for safety — so they can stop believing it's unsafe to let people in and finally build connected, lasting relationships.";

/**
 * Shared narrative-style specialty landing page for hyper-independent women
 * in a specific city. Config-driven so each city gets tailored geography
 * copy and license credentials without duplicating 400+ lines of layout.
 */
export const SpecialtyLocationPage = ({ config }) => {
  const {
    pageSeoKey,
    pathname,
    city,
    state,
    licenseRole,
    licenseNumber,
    alsoLicensedIn,
    seoTitle,
    seoDescription,
    seoImageAlt,
    heroSubheading,
    home,
    stateLabel,
    testId,
  } = config;

  const seoOverride = usePageSeo(pageSeoKey);
  const description = seoOverride?.meta_description || seoDescription;
  const imageAlt = seoOverride?.image_alt || seoImageAlt;

  return (
    <div className="pt-20" data-testid={testId}>
      <SEO
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        title={seoTitle}
        description={description}
        image={LAURA_IMAGE}
        imageAlt={imageAlt}
        pathname={pathname}
      />

      {/* === HERO === */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-8"
          >
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium"
              data-testid="specialty-location-pill"
            >
              <MapPin className="h-4 w-4" />
              Serving women in {city}, {state} — fully online
            </span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2 flex justify-center lg:justify-start"
            >
              <div className="relative">
                <div className="absolute -inset-3 rounded-3xl bg-primary/10 -z-10"></div>
                <img
                  src={LAURA_IMAGE}
                  alt={`Laura A. Jeanis, LCSW, LICSW, CCTP, CEO — Licensed Psychotherapist specializing in Online Ketamine-Assisted Psychotherapy for hyper-independent women in ${city}, ${state}`}
                  className="rounded-3xl shadow-soft object-cover w-full max-w-md aspect-[4/5]"
                  data-testid="specialty-laura-portrait"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="lg:col-span-3"
            >
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-tight">
                {config.h1Override ? (
                  config.h1Override
                ) : (
                  <>
                    Virtual Ketamine-Assisted Therapy for Hyper-Independent Women in{" "}
                    <span className="text-primary">{city}, {state}</span>
                  </>
                )}
              </h1>
              <h2 className="mt-4 font-serif text-xl sm:text-2xl lg:text-3xl italic font-medium text-primary leading-snug">
                {heroSubheading}
              </h2>

              {config.heroBio ? (
                <div className="mt-6 text-base md:text-lg text-foreground/80 leading-[1.85]" data-testid="specialty-hero-bio">
                  {config.heroBio.map((p) => (
                    <p key={p.slice(0, 40)} className="mb-5 last:mb-0">{p}</p>
                  ))}
                </div>
              ) : (
              <div className="mt-6 space-y-2" data-testid="specialty-credentials">
                <p className="font-serif text-xl font-semibold text-foreground">
                  Laura A. Jeanis, LCSW, LICSW, CCTP, CEO
                </p>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Licensed Psychotherapist · Certified Clinical Trauma
                  Professional · 11+ years of clinical experience
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 mt-3">
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0" />
                    {licenseRole} — License No. {licenseNumber}
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0" />
                    Also licensed in {alsoLicensedIn}
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0" />
                    Certified Clinical Trauma Professional (CCTP) · No. 244805
                  </li>
                </ul>
              </div>
              )}

              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/book">
                  <Button
                    className="rounded-full px-8 py-6 text-base bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="specialty-hero-cta"
                  >
                    Book Your Free Consultation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="rounded-full px-8 py-6 text-base border-primary/30 text-primary hover:bg-primary/5"
                  >
                    Ask Laura a Question
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* === Optional city-specific narrative === */}
      {config.cityNarrative && (
        <section
          className="py-20 md:py-24 px-6 md:px-12 bg-secondary/20"
          data-testid="specialty-city-narrative"
        >
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h2
                className="font-serif text-3xl sm:text-4xl md:text-[2.75rem] font-semibold text-foreground leading-tight tracking-tight"
                data-testid="specialty-city-narrative-heading"
              >
                {config.cityNarrative.heading}
              </h2>
              <div className="mt-8 space-y-6 text-base md:text-lg text-foreground/80 leading-[1.85]">
                {config.cityNarrative.paragraphs.map((para, idx) => (
                  <p key={idx} data-testid={`specialty-city-narrative-p-${idx}`}>
                    {para}
                  </p>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* === Impact Statement === */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center"
            data-testid="specialty-impact-statement"
          >
            <Quote className="h-12 w-12 text-primary/30 mx-auto mb-6" />
            <blockquote className="font-serif text-2xl md:text-3xl lg:text-[2.5rem] font-medium text-foreground leading-[1.35] tracking-tight">
              <span className="italic">&ldquo;{IMPACT_STATEMENT}&rdquo;</span>
            </blockquote>
            <p className="mt-8 text-sm uppercase tracking-[0.2em] text-muted-foreground">
              — Laura A. Jeanis, LCSW, LICSW, CCTP
            </p>
          </motion.div>
        </div>
      </section>

      {/* === "Is this you?" archetype === */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 text-primary font-medium text-sm uppercase tracking-[0.18em]">
              <Sparkles className="h-4 w-4" />
              Is this you?
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mt-4 leading-tight">
              You&rsquo;ve been called &ldquo;the strong one&rdquo; your whole life —
              and quietly, you&rsquo;re exhausted.
            </h2>

            <div className="mt-8 space-y-5 text-lg text-muted-foreground leading-relaxed">
              <p>
                You handle everything. The kids, the career, the family
                emergencies, the emotional labor no one else notices. From the
                outside, you look like you&rsquo;ve got it all together — because
                you do. That&rsquo;s the problem.
              </p>
              <p>
                Somewhere along the way, you learned that needing people was
                dangerous. So you stopped. You became the woman who doesn&rsquo;t
                ask, doesn&rsquo;t lean, doesn&rsquo;t let anyone see the parts of
                her that are still tender.
              </p>
              <p>
                And now — even in the quiet of your {home}, everything
                &ldquo;fine&rdquo; on paper — there&rsquo;s a heaviness you
                can&rsquo;t name. A longing to be held, to be known, to finally set
                the weight down.
              </p>
            </div>

            <ul className="mt-8 space-y-3 text-foreground/90">
              {[
                "You struggle to ask for help — even when you’re drowning.",
                "You over-function, over-give, and then quietly resent it.",
                "Talk therapy has helped you understand your patterns, but not release them.",
                "You’re spiritual, self-aware, and ready for something deeper than insight.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-2 h-2 w-2 rounded-full bg-accent flex-shrink-0"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* === Lead Magnet === */}
      <WorryLessChapterOptIn />

      {/* === What is KAP? === */}
      <WhatIsKAP background="bg-white" />

      {/* === What sessions look like === */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-soft aspect-[4/5] lg:aspect-[5/6]">
                <img
                  src={THEME_IMAGE}
                  alt={`A hyper-independent woman resting peacefully at home in ${city}, ${state} wearing an eye mask and headphones during an online ketamine-assisted psychotherapy session`}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="specialty-theme-image"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <span className="inline-flex items-center gap-2 text-primary font-medium text-sm uppercase tracking-[0.18em]">
                <Sparkles className="h-4 w-4" />
                What sessions look like
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mt-4 leading-tight">
                Finally, a place to lay it all down — from your own home in {city}
              </h2>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                You settle in somewhere quiet — the corner chair, your bed, a nook
                by the window. A soft blanket, an eye mask, headphones with curated
                music. Laura is with you on a secure video connection, holding the
                space while the medicine gently softens what your armor has been
                guarding.
              </p>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                No commute. No pretending to be pulled-together in a waiting room.
                No performing strength on the way home. Just you, your inner world,
                and a therapist trained to walk with you into the places talk
                therapy alone can&rsquo;t reach.
              </p>

              <ul className="mt-8 space-y-3">
                {[
                  `100% online — meet from anywhere in ${stateLabel}`,
                  "Trauma-informed, spiritually integrated approach",
                  "Built for the woman who’s tired of being everyone’s rock",
                  "Personalized integration support between sessions",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-foreground/90"
                  >
                    <span className="mt-2 h-2 w-2 rounded-full bg-accent flex-shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-border/40">
                <Link
                  to="/blog"
                  className="group inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
                  data-testid="specialty-blog-link"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>
                    Curious how ketamine actually works? Read more on the blog
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* === Cross-link to Hyper-Independence specialty === */}
      <section className="py-8 px-6 md:px-12 bg-secondary/20" data-testid="specialty-cross-link-hyperindependence">
        <div className="max-w-3xl mx-auto space-y-3">
          <Link
            to="/specialties/therapy-for-hyper-independence"
            className="group flex items-center justify-between gap-4 p-5 md:p-6 bg-white border border-border/40 rounded-2xl hover:border-primary/40 transition-colors"
          >
            <p className="text-sm md:text-base text-foreground/80 leading-relaxed">
              Struggling with hyper-independence in relationships?{" "}
              <span className="text-primary font-semibold">Read more</span>
            </p>
            <ArrowRight className="h-5 w-5 text-primary flex-shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/specialties/boundaries-therapist-emotionally-immature-parents"
            className="group flex items-center justify-between gap-4 p-5 md:p-6 bg-white border border-border/40 rounded-2xl hover:border-primary/40 transition-colors"
            data-testid="specialty-cross-link-boundaries"
          >
            <p className="text-sm md:text-base text-foreground/80 leading-relaxed">
              Struggling with emotionally immature parents?{" "}
              <span className="text-primary font-semibold">Read more</span>
            </p>
            <ArrowRight className="h-5 w-5 text-primary flex-shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* === CareCredit Financing Banner === */}
      <CareCreditBanner variant="muted" />

      {/* === Closing CTA === */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground leading-tight">
              You&rsquo;ve been strong long enough. It&rsquo;s time to be held.
            </h2>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              Book a free 40-minute consultation with Laura. No pressure — just an
              honest conversation about whether online Ketamine-Assisted
              Psychotherapy is the right next step for you.
            </p>
            <div className="mt-10">
              <Link to="/book">
                <Button
                  className="rounded-full px-10 py-7 text-lg bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="specialty-closing-cta"
                >
                  Book Your Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SpecialtyLocationPage;
